/**
 * Daily bars (e.g. ad spend) with a line (e.g. profit after ads) on one money axis — uPlot.
 * Canvas can't read CSS variables, so colours are resolved from the tokens at draw time
 * and the chart is rebuilt when the theme changes.
 */
import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

const token = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888";
const money = (v: number) => {
  const a = Math.abs(v);
  const s = a >= 1000 ? `$${(a / 1000).toFixed(1)}k` : `$${a.toFixed(0)}`;
  return v < 0 ? `−${s}` : s;
};

export function BarLineChart(p: {
  dates: string[];
  bars: number[];
  line: number[];
  barLabel: string;
  lineLabel: string;
  height?: number;
  title: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let plot: uPlot | null = null;
    const build = () => {
      plot?.destroy();
      const xs = p.dates.map((d) => Date.parse(`${d}T12:00:00Z`) / 1000);
      const grid = { stroke: token("--grid"), width: 1 };
      plot = new uPlot(
        {
          width: el.clientWidth || 600,
          height: p.height ?? 240,
          legend: { show: false },
          cursor: { points: { show: false } },
          scales: {
            x: { time: true },
            y: { range: (_u, lo, hi) => [Math.min(0, lo), Math.max(1, hi) * 1.08] },
          },
          axes: [
            {
              stroke: token("--muted"),
              grid: { show: false },
              ticks: { show: false },
              font: `11px ${token("--mono")}`,
            },
            {
              stroke: token("--muted"),
              grid,
              ticks: { show: false },
              values: (_u, vals) => vals.map(money),
              size: 56,
              font: `11px ${token("--mono")}`,
            },
          ],
          series: [
            {},
            {
              label: p.barLabel,
              fill: token("--s2"),
              stroke: token("--s2"),
              paths: uPlot.paths.bars!({ size: [0.62, 40] }),
              points: { show: false },
              value: (_u, v) => money(v ?? 0),
            },
            {
              label: p.lineLabel,
              stroke: token("--profit"),
              width: 2,
              points: { show: false },
              value: (_u, v) => money(v ?? 0),
            },
          ],
        },
        [xs, p.bars, p.line],
        el,
      );
    };
    build();
    const ro = new ResizeObserver(() => plot?.setSize({ width: el.clientWidth, height: p.height ?? 240 }));
    ro.observe(el);
    const mo = new MutationObserver(build);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
      plot?.destroy();
    };
  }, [p.dates, p.bars, p.line, p.barLabel, p.lineLabel, p.height]);

  return (
    <div ref={ref} role="img" aria-label={`${p.title}: ${p.barLabel} as bars, ${p.lineLabel} as a line`} />
  );
}
